import type {
  StructureData,
  Ou,
  Group,
  User,
  GroupType,
  ItemSource,
  OuKind,
} from './types.ts'

const SYSTEMHAUS_OU_NAME = '_IMP'
const MANDATORY_CHILD_NAMES: ReadonlyArray<string> = ['AB', 'LW', 'SW', 'SA', 'FU']

/**
 * Namens-Praefixe, die global-verwaltete Standard-Gruppen kennzeichnen.
 * Quelle: Backend-Seeder `DEFAULT_STANDARD_GROUPS` in routes/it-structures.ts.
 */
const GLOBAL_GROUP_PREFIXES: ReadonlyArray<string> = ['_IMP_', '_LIC_']

function inferOuKind(ou: Ou, domainPrefix: string | undefined, customerRootId: string | null): OuKind {
  const nameLower = ou.name.trim().toLowerCase()
  if (ou.parent === null && nameLower === SYSTEMHAUS_OU_NAME.toLowerCase()) return 'systemhaus'
  if (domainPrefix && ou.parent === null && nameLower === domainPrefix.toLowerCase()) return 'customer-root'
  if (
    customerRootId &&
    ou.parent === customerRootId &&
    MANDATORY_CHILD_NAMES.some((m) => m.toLowerCase() === nameLower)
  )
    return 'mandatory'
  return 'regular'
}

function inferOuSource(kind: OuKind): ItemSource {
  return kind === 'customer-root' || kind === 'mandatory' || kind === 'systemhaus' ? 'global' : 'custom'
}

function isGlobalGroupName(name: string): boolean {
  return GLOBAL_GROUP_PREFIXES.some((p) => name.startsWith(p))
}

/**
 * Fallback-OU fuer eine Gruppe, wenn `ouId` fehlt.
 *  - Standard-Gruppen (`_IMP_*` / `_LIC_*`) → _IMP-OU
 *  - Sonst: gleichnamige Pflicht-OU unter Kunden-Root (AB/LW/SW/SA/FU ↔ group.type)
 *  - Fallback: Kunden-Root
 */
function inferGroupOuId(
  group: Group,
  impOuId: string | null,
  mandatoryOuByType: Map<GroupType, string>,
  customerRootId: string | null,
): string {
  if (isGlobalGroupName(group.name) && impOuId) return impOuId
  const matchByType = mandatoryOuByType.get(group.type)
  if (matchByType) return matchByType
  return customerRootId ?? impOuId ?? ''
}

/**
 * Normalisiert eine geladene `StructureData` so, dass die vom AD-Explorer
 * benoetigten Felder (`ouId`, `source`, `kind`) garantiert gesetzt sind.
 *
 * Wird einmalig beim Laden durchlaufen. Altdatensaetze erhalten per Heuristik
 * sinnvolle Defaults; neu angelegte Items setzen die Felder direkt.
 * Ist idempotent — bereits normalisierte Daten bleiben unveraendert.
 */
export function normalizeStructureData(raw: StructureData): StructureData {
  const domainPrefix = (raw.domainPrefix ?? '').trim() || undefined

  const customerRoot = raw.ous.find(
    (o) =>
      o.parent === null &&
      domainPrefix !== undefined &&
      o.name.trim().toLowerCase() === domainPrefix.toLowerCase(),
  )
  const customerRootId = customerRoot?.id ?? null

  const impOu = raw.ous.find(
    (o) => o.parent === null && o.name.trim().toLowerCase() === SYSTEMHAUS_OU_NAME.toLowerCase(),
  )
  const impOuId = impOu?.id ?? null

  const mandatoryOuByType = new Map<GroupType, string>()
  if (customerRootId) {
    for (const ou of raw.ous) {
      if (ou.parent !== customerRootId) continue
      const name = ou.name.trim().toUpperCase()
      if ((MANDATORY_CHILD_NAMES as ReadonlyArray<string>).includes(name)) {
        mandatoryOuByType.set(name as GroupType, ou.id)
      }
    }
  }

  const normalizedOus: Ou[] = raw.ous.map((ou) => {
    const kind = ou.kind ?? inferOuKind(ou, domainPrefix, customerRootId)
    const source = ou.source ?? inferOuSource(kind)
    return { ...ou, kind, source }
  })

  const normalizedGroups: Group[] = raw.groups.map((g) => {
    const ouId = g.ouId ?? inferGroupOuId(g, impOuId, mandatoryOuByType, customerRootId)
    const source: ItemSource = g.source ?? (isGlobalGroupName(g.name) ? 'global' : 'custom')
    return { ...g, ouId, source }
  })

  const userFallbackOuId = customerRootId ?? impOuId ?? ''
  const normalizedUsers: User[] = raw.users.map((u) => ({
    ...u,
    ouId: u.ouId ?? userFallbackOuId,
    source: u.source ?? 'custom',
  }))

  return {
    ...raw,
    ous: normalizedOus,
    groups: normalizedGroups,
    users: normalizedUsers,
  }
}
